import { Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type LocalModelSpecsFragment } from '../../../../api/fragments/LocalModelSpecs';
import { useGetVehicleQuery } from '../../../../api/queries/getVehicle';
import { type LocalSubmodelSettings, VehicleKind } from '../../../../api/types';
import VehicleSelectField from '../../../../components/fields/VehicleSelectField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useVehicleBodyTypeOptions from '../../../../utilities/useVehicleBodyTypeOptions';

export type AddSubmodelDetailsProps = {
    initialValues: LocalSubmodelSettings;
    updated?: string;
    offset?: string;
    disabled?: boolean;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const AddSubmodelDetails = ({ initialValues, updated, offset, disabled = false }: AddSubmodelDetailsProps) => {
    const { t } = useTranslation('submodel');
    const { yesNoSwitch } = useSystemSwitchData();
    const { values, setFieldValue } = useFormikContext<LocalSubmodelSettings>();
    const bodyTypeOptions = useVehicleBodyTypeOptions();
    const { data, loading } = useGetVehicleQuery({
        fetchPolicy: 'cache-and-network',
        variables: { id: values.parentModelId },
        skip: initialValues.parentModelId === values.parentModelId || !values.parentModelId,
    });

    const parentModel = useMemo(() => data?.vehicle as LocalModelSpecsFragment, [data?.vehicle]);

    useEffect(() => {
        if (parentModel) {
            setFieldValue('bodyType', parentModel.bodyType);
        }
    }, [parentModel, setFieldValue]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('submodel:cardTitles.mainDetails')}>
                <Panel key={t('submodel:cardTitles.mainDetails')} header={t('submodel:cardTitles.mainDetails')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <VehicleSelectField
                                {...t('submodel:fields.model', { returnObjects: true })}
                                autoSelected={!updated}
                                disabled={disabled}
                                name="parentModelId"
                                vehicleKind={VehicleKind.LocalModel}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('submodel:fields.name', { returnObjects: true })}
                                disabled={disabled}
                                name="name"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('submodel:fields.identifier', { returnObjects: true })}
                                disabled={disabled}
                                name="identifier"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('submodel:fields.bodyType', { returnObjects: true })}
                                disabled={loading || disabled}
                                name="bodyType"
                                options={bodyTypeOptions}
                                required
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('submodel:fields.order', { returnObjects: true })}
                                disabled={disabled}
                                min={1}
                                name="order"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('submodel:fields.isActive', { returnObjects: true })}
                                disabled={disabled}
                                name="isActive"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                        {updated && (
                            <Col {...colSpan}>
                                <FormFields.DisplayFieldWithUTCOffset
                                    {...t('common:fields.updated', { returnObjects: true, offset })}
                                    value={updated}
                                />
                            </Col>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default AddSubmodelDetails;
